/**
 * src/hooks/useWallet.js — Phantom/Solflare wallet connection.
 *
 * Handles:
 *   - Wallet connection (Phantom, Solflare, etc.)
 *   - Sign-In With Solana (SIWS) authentication
 *   - Session persistence via server JWT
 *   - Balance checking
 *   - SOL deposit transactions
 */

import { useState, useEffect, useCallback } from 'react';
import { requestNonce, verifySignature, logout, getMe } from '../lib/api';

/**
 * Custom hook for Solana wallet auth + operations.
 *
 * @returns {{
 *   user: object|null,
 *   connected: boolean,
 *   connecting: boolean,
 *   error: string|null,
 *   connectWallet: Function,
 *   disconnectWallet: Function,
 *   sendDeposit: Function,
 * }}
 */
export default function useWallet() {
    const [user, setUser] = useState(null);
    const [connected, setConnected] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Connect Phantom/Solflare wallet and authenticate.
     * Flow: connect → request nonce → sign → verify → JWT
     */
    const connectWallet = useCallback(async () => {
        setConnecting(true);
        setError(null);

        try {
            // 1. Check for Phantom
            const provider = getPhantomProvider();
            if (!provider) {
                setError('Phantom wallet not found. Install at phantom.app');
                setConnecting(false);
                return;
            }

            // 2. Connect wallet
            const resp = await provider.connect();
            const walletAddress = resp.publicKey.toString();

            // 3. Request nonce from server
            const { message, nonce } = await requestNonce(walletAddress);

            // 4. Sign the message with wallet
            const encodedMessage = new TextEncoder().encode(message);
            const signedMessage = await provider.signMessage(encodedMessage, 'utf8');

            // Convert signature to base58
            const signature = encodeBase58(signedMessage.signature);

            // 5. Verify with server — server sets httpOnly JWT cookie
            const data = await verifySignature(walletAddress, signature, nonce);

            setUser(data.user);
            setConnected(true);
        } catch (err) {
            console.error('[Wallet] Connect error:', err);
            if (err.message?.includes('User rejected')) {
                setError('Wallet connection cancelled');
            } else {
                setError(err.message || 'Failed to connect wallet');
            }
        } finally {
            setConnecting(false);
        }
    }, []);

    /**
     * Disconnect wallet and clear session.
     */
    const disconnectWallet = useCallback(async () => {
        try {
            await logout();
            const provider = getPhantomProvider();
            if (provider) await provider.disconnect();
        } catch { /* ignore */ }

        setUser(null);
        setConnected(false);
        setError(null);
    }, []);

    /**
     * Send SOL deposit to house wallet.
     * @param {number} amountSol - Amount to deposit
     * @param {string} houseWalletPubkey - House wallet address
     * @returns {Promise<string>} Transaction signature
     */
    const sendDeposit = useCallback(async (amountSol, houseWalletPubkey) => {
        const provider = getPhantomProvider();
        if (!provider?.publicKey) {
            throw new Error('Wallet not connected');
        }

        const { Connection, PublicKey, Transaction, SystemProgram, LAMPORTS_PER_SOL } =
            await import('@solana/web3.js');

        const connection = new Connection(
            process.env.REACT_APP_SOLANA_RPC || 'https://api.mainnet-beta.solana.com',
            'confirmed'
        );

        const lamports = Math.floor(amountSol * LAMPORTS_PER_SOL);
        const transaction = new Transaction().add(
            SystemProgram.transfer({
                fromPubkey: provider.publicKey,
                toPubkey: new PublicKey(houseWalletPubkey),
                lamports,
            })
        );

        transaction.feePayer = provider.publicKey;
        const { blockhash } = await connection.getLatestBlockhash();
        transaction.recentBlockhash = blockhash;

        const signed = await provider.signTransaction(transaction);
        const signature = await connection.sendRawTransaction(signed.serialize());
        await connection.confirmTransaction(signature, 'confirmed');

        return signature;
    }, []);

    return {
        user,
        connected,
        connecting,
        error,
        connectWallet,
        disconnectWallet,
        sendDeposit,
    };
}

/** Get Phantom provider from window. */
function getPhantomProvider() {
    if (typeof window !== 'undefined' && window.solana?.isPhantom) {
        return window.solana;
    }
    return null;
}

/** Encode Uint8Array to base58 string. */
function encodeBase58(bytes) {
    const ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
    let result = '';
    /* eslint-disable no-undef */
    let num = BigInt('0x' + Buffer.from(bytes).toString('hex'));
    while (num > 0n) {
        result = ALPHABET[Number(num % 58n)] + result;
        num = num / 58n;
    }
    /* eslint-enable no-undef */
    for (const byte of bytes) {
        if (byte === 0) result = '1' + result;
        else break;
    }
    return result;
}
